import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Filter,
  Flag,
  Flame,
  Info,
  Mail,
  Megaphone,
  Target,
  Trophy,
  Users,
} from "lucide-react";
import { seguimientoApi } from "./api";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useToast } from "@/app/providers/Toaster";
import { Avatar, Badge } from "./ui";
import { STAGE_LABELS, STAGE_TONE, RESULT_LABELS, RESULT_TONE } from "./labels";
import type { ObjetivoMetric, ProspectStage, ValidationResult } from "./types";

/** Ícono (i) con tooltip nativo: qué mide la métrica y para qué. Usa `title`
 *  (no se recorta dentro de tarjetas con overflow-hidden como las tablas). */
function InfoTip({ text, className }: { text: string; className?: string }) {
  return (
    <span
      title={text}
      aria-label={text}
      className={`inline-flex shrink-0 cursor-help text-fg-subtle hover:text-fg-muted ${className ?? ""}`}
    >
      <Info size={13} />
    </span>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone,
  info,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "danger" | "warning";
  info?: string;
}) {
  const color = tone === "danger" ? "text-danger" : tone === "warning" ? "text-warning" : "text-fg";
  return (
    <div className="bg-bg-subtle rounded-md p-3">
      <div className="text-2xs text-fg-muted flex items-center gap-1">
        {label}
        {info && <InfoTip text={info} className="ml-auto" />}
      </div>
      <div className={`text-2xl font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-2xs text-fg-subtle mt-0.5">{sub}</div>}
    </div>
  );
}

/** Barra de progreso etiquetada (tasas de invitación). */
function RateBar({ label, pct }: { label: string; pct: number }) {
  const w = Math.max(0, Math.min(100, pct)); // clamp defensivo
  return (
    <div className="flex items-center gap-3 mb-1.5">
      <span className="text-2xs text-fg-muted w-28 shrink-0">{label}</span>
      <div className="h-2 flex-1 rounded-full bg-bg-subtle overflow-hidden">
        <div className="h-full rounded-full bg-accent" style={{ width: `${w}%` }} />
      </div>
      <span className="text-2xs tabular-nums text-fg-muted w-9 text-right shrink-0">{pct}%</span>
    </div>
  );
}

// Centro real para columnas numéricas: el `.nx-table thead th` global fuerza
// text-align:left con más especificidad que la utility `text-center`, así que
// el header se centra con inline-style (gana sobre la regla CSS).
const TH_CENTER = { textAlign: "center" } as const;

/** Fila de objetivo por segmento: pipeline → activados vs meta editable. */
function ObjetivoRow({ o, onSave }: { o: ObjetivoMetric; onSave: (target: number) => void }) {
  const [meta, setMeta] = useState(String(o.objetivo || ""));
  const focused = useRef(false);
  // Resync si el backend cambia la meta — pero NO mientras el usuario escribe.
  useEffect(() => {
    if (!focused.current) setMeta(String(o.objetivo || ""));
  }, [o.objetivo]);
  const pct = Math.max(0, Math.min(100, o.pct));
  const commit = () => {
    focused.current = false;
    const raw = meta.trim();
    const n = raw === "" ? 0 : parseInt(raw, 10); // vacío = sin meta (0)
    if (Number.isFinite(n) && n >= 0) {
      if (n !== o.objetivo) onSave(n);
      setMeta(String(n || "")); // normaliza ("007"→"7", 0→"")
    } else {
      setMeta(String(o.objetivo || "")); // inválido → volver al valor real
    }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-fg w-28 shrink-0">{o.label}</span>
        <span className="text-2xs text-fg-subtle">
          {o.enPipeline} en pipeline ·{" "}
          <span className="text-fg-muted font-medium">{o.activados} activados</span>
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-2xs text-fg-subtle">
          meta
          <input
            type="number"
            min={0}
            value={meta}
            onFocus={() => (focused.current = true)}
            onChange={(e) => setMeta(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
            className="nx-input w-16 py-0.5 text-xs text-right"
            placeholder="—"
          />
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-2.5 flex-1 rounded-full bg-bg-subtle overflow-hidden">
          <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-2xs tabular-nums text-fg-muted w-28 text-right shrink-0">
          {o.activados}/{o.objetivo || "—"} · {o.objetivo > 0 ? `${o.pct}%` : "sin meta"}
        </span>
      </div>
    </div>
  );
}

// Estado de módulos: orden de mejor a peor para el desglose.
const MODULE_STATUS_ORDER: ValidationResult[] = [
  "funciona_ok",
  "funciona_con_obs",
  "en_prueba",
  "no_probado",
  "falla",
];

export function MetricasPane() {
  const metricsQuery = useQuery({ queryKey: ["seg", "metrics"], queryFn: seguimientoApi.metrics });
  const alertsQuery = useQuery({ queryKey: ["seg", "alerts"], queryFn: seguimientoApi.alerts });
  const qc = useQueryClient();
  const { push } = useToast();
  const setGoal = useMutation({
    mutationFn: ({ segment, target }: { segment: string; target: number }) =>
      seguimientoApi.setGoal(segment, target),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["seg", "metrics"] });
      push({ kind: "success", message: "Meta actualizada." });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "No se pudo guardar la meta." }),
  });

  if (metricsQuery.isLoading) return <LoadingBlock label="Calculando métricas…" />;
  if (metricsQuery.isError || !metricsQuery.data)
    return <ErrorBlock onRetry={() => metricsQuery.refetch()} />;

  const m = metricsQuery.data;
  // El funnel no muestra "Descartado" si está vacío (igual que el Excel).
  const matrixRows = m.funnel.matrix.filter((r) => r.stage !== "descartado" || r.total > 0);

  // Conversión del pipeline + invitaciones + origen (?? por la ventana de deploy).
  const conv = m.conversion;
  const inv = m.invitations;
  // Etiquetas en "altitud de etapa" (llegó AL MENOS hasta…). No dicen "invitados"
  // ni "abrieron" porque la etapa se puede mover a mano sin invitación real — eso
  // contradiría la tarjeta de Invitaciones (que sí sale de la tabla de invites).
  const convSteps = conv
    ? [
        { label: "Prospectos", value: conv.total },
        { label: "Contactados", value: conv.contactados },
        { label: "Interesados", value: conv.interesados },
        { label: "Correo enviado", value: conv.invitados },
        { label: "Perfil iniciado", value: conv.abrieron },
        { label: "Activaron", value: conv.activaron },
      ]
    : [];
  const convTop = conv?.total || 0;
  const bySource = Object.entries(m.funnel.bySource ?? {}).sort((a, b) => b[1] - a[1]);
  const sourceTotal = bySource.reduce((s, [, n]) => s + n, 0);
  const objetivos = m.objetivos ?? [];

  // Performance de tareas (keys nuevas; defendidas con ?? [] por la ventana de deploy).
  const byAssignee = m.byAssignee ?? [];
  const byBusinessArea = m.byBusinessArea ?? [];
  const bySprintActive = m.bySprintActive ?? [];
  const sprintHistory = m.sprintHistory ?? [];
  // Escala del chart "Velocity histórico" = solo sprints cerrados (es lo único
  // que ese gráfico pinta; mezclar velocity de sprints activos comprimiría las
  // barras contra un valor que no se dibuja).
  const maxVel = Math.max(1, ...sprintHistory.map((h) => h.performanceSnapshot?.velocity ?? 0));
  // "Aportes por cada uno" = trabajo del Kanban por persona (tareas), NO leads.

  return (
    <div className="flex flex-col gap-4">
      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Avance MVP"
          value={`${m.mvp.pct}%`}
          sub={`${m.mvp.doneTasks} / ${m.mvp.totalTasks} tareas`}
          info="% de tareas comprometidas (sin backlog) que están hechas. Mide cuánto del trabajo planificado ya está cerrado."
        />
        <StatCard
          label="Módulos validados"
          value={`${m.modules.pct}%`}
          sub={`${m.modules.validated} / ${m.modules.total}`}
          info="% de módulos del catálogo marcados como funcionando. Mide cuánto de la plataforma está probado y OK."
        />
        <StatCard
          label="Prospectos"
          value={`${m.funnel.total}`}
          sub="en el funnel"
          info="Total de prospectos vivos en el funnel (artistas + productores + proveedores)."
        />
        <StatCard
          label="Por seguir"
          value={`${m.funnel.alertCount}`}
          sub="+5 días sin contacto"
          tone={m.funnel.alertCount > 0 ? "danger" : undefined}
          info="Prospectos con +5 días sin contacto y sin recordatorio pospuesto. Los que hay que retomar ya."
        />
      </div>

      {/* ── Objetivos del equipo por segmento ── */}
      {objetivos.length > 0 && (
        <div className="nx-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Flag size={15} className="text-accent" />
            <h2 className="nx-h2">Objetivo global del equipo</h2>
            <InfoTip
              text="Meta de largo plazo por segmento: cuántos artistas/productores/proveedores queremos sumar. Muestra pipeline → activados vs la meta. Cada hito aporta su parte."
              className="ml-auto"
            />
          </div>
          <p className="text-2xs text-fg-subtle mb-3">
            El norte de largo plazo, por segmento (pipeline → activados vs meta). Cada hito aporta su
            parte — ver "Sprint activo". Editá la meta cuando quieras.
          </p>
          <div className="flex flex-col gap-3.5">
            {objetivos.map((o) => (
              <ObjetivoRow
                key={o.segment}
                o={o}
                onSave={(target) => setGoal.mutate({ segment: o.segment, target })}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Conversión del pipeline + Invitaciones ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {conv && (
          <div className="nx-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Filter size={15} className="text-accent" />
              <h2 className="nx-h2">Conversión del pipeline</h2>
              <InfoTip
                text="Embudo acumulado: cuántos prospectos llegaron al menos hasta cada etapa. Sirve para ver en qué paso se cae la gente."
                className="ml-auto"
              />
            </div>
            <p className="text-2xs text-fg-subtle mb-3">
              Cuántos llegaron al menos hasta cada etapa (excluye descartados).
            </p>
            <div className="flex flex-col gap-2">
              {convSteps.map((s, i) => {
                const pct = convTop ? Math.round((s.value / convTop) * 100) : 0;
                // Caída respecto del paso anterior.
                const prev = i > 0 ? convSteps[i - 1].value : s.value;
                const drop = prev > 0 ? Math.round(((prev - s.value) / prev) * 100) : 0;
                return (
                  <div key={s.label} className="flex items-center gap-3">
                    <span className="text-xs text-fg w-32 shrink-0 truncate">{s.label}</span>
                    <div className="h-2 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-2xs tabular-nums text-fg-muted w-24 text-right shrink-0">
                      {s.value} · {pct}%
                      {i > 0 && drop > 0 && (
                        <span className="text-danger"> −{drop}%</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            {conv.descartados > 0 && (
              <p className="text-2xs text-fg-subtle mt-2">
                {conv.descartados} descartado{conv.descartados === 1 ? "" : "s"} (fuera del funnel).
              </p>
            )}
          </div>
        )}

        {inv && (
          <div className="nx-card p-4">
            <div className="flex items-center gap-2 mb-1">
              <Mail size={15} className="text-accent" />
              <h2 className="nx-h2">Invitaciones de onboarding</h2>
              <InfoTip
                text="Números del correo de activación: enviadas, abiertas, activadas, vencidas + tasas. Mide qué tan bien convierte la invitación."
                className="ml-auto"
              />
            </div>
            <p className="text-2xs text-fg-subtle mb-3">
              Números duros del correo de activación (artistas / productores / proveedores).
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-bg-subtle rounded-md p-2.5">
                <div className="text-2xs text-fg-muted">Enviadas</div>
                <div className="text-xl font-semibold text-fg tabular-nums">{inv.total}</div>
              </div>
              <div className="bg-bg-subtle rounded-md p-2.5">
                <div className="text-2xs text-fg-muted">Activadas</div>
                <div className="text-xl font-semibold text-success tabular-nums">{inv.accepted}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-2xs text-fg-muted mb-3">
              <span>Pendientes: {inv.invited}</span>
              <span>Abrieron: {inv.opened}</span>
              <span>Vencidas: {inv.expired}</span>
              <span>Anuladas: {inv.revoked}</span>
            </div>
            <RateBar label="Tasa de apertura" pct={inv.tasaApertura} />
            <RateBar label="Tasa de activación" pct={inv.tasaActivacion} />
            {inv.avgDaysToAccept != null && (
              <p className="text-2xs text-fg-subtle mt-2">
                Tiempo medio de activación: {inv.avgDaysToAccept} día
                {inv.avgDaysToAccept === 1 ? "" : "s"}.
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Prospectos por origen / canal ── */}
      {bySource.length > 0 && (
        <div className="nx-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Megaphone size={15} className="text-accent" />
            <h2 className="nx-h2">Prospectos por origen</h2>
            <InfoTip
              text="De qué canal llegó cada lead (Instagram, referido, evento…). Sirve para saber qué canal trae más prospectos."
              className="ml-auto"
            />
          </div>
          <p className="text-2xs text-fg-subtle mb-3">De qué canal llegó cada lead.</p>
          <div className="flex flex-col gap-2">
            {bySource.map(([src, n]) => {
              const pct = sourceTotal ? Math.round((n / sourceTotal) * 100) : 0;
              return (
                <div key={src} className="flex items-center gap-3">
                  <span className="text-sm text-fg w-32 shrink-0 truncate">{src}</span>
                  <div className="h-2 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                    <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-2xs tabular-nums text-fg-muted w-16 text-right shrink-0">
                    {n} · {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Por atender ahora */}
      <div className="nx-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={15} className="text-warning" />
          <h2 className="nx-h2">Por atender ahora</h2>
          <InfoTip
            text="La cola del día: prospectos atrasados (+5 días sin contacto) + inscritos que aún no entraron a la plataforma."
            className="ml-auto"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-bg-subtle rounded-md p-3">
            <div className="text-2xs text-fg-muted">Prospectos en ⚠ seguir</div>
            <div className={`text-xl font-semibold ${m.funnel.alertCount > 0 ? "text-danger" : "text-fg"}`}>
              {m.funnel.alertCount}
            </div>
          </div>
          <div className="bg-bg-subtle rounded-md p-3">
            <div className="text-2xs text-fg-muted inline-flex items-center gap-1">
              <Flame size={11} /> Inscritos por activar
            </div>
            <div className={`text-xl font-semibold ${m.funnel.inscritosPorActivar > 0 ? "text-warning" : "text-fg"}`}>
              {m.funnel.inscritosPorActivar}
            </div>
            <div className="text-2xs text-fg-subtle mt-0.5">de {m.funnel.activosTotal} activos</div>
          </div>
        </div>

        {alertsQuery.isLoading ? (
          <p className="text-sm text-fg-subtle">Cargando…</p>
        ) : (alertsQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-subtle">Nadie en ⚠ seguir. Todo al día o pospuesto.</p>
        ) : (
          <div className="flex flex-col divide-y divide-border">
            {(alertsQuery.data ?? []).map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2 text-sm">
                <span className="font-medium text-fg">{p.name}</span>
                {p.responsable && (
                  <span className="text-2xs text-fg-subtle">· {p.responsable.name}</span>
                )}
                <span className="ml-auto flex items-center gap-2">
                  {p.nextAction && (
                    <span className="text-2xs text-fg-muted truncate max-w-[260px]">
                      {p.nextAction}
                    </span>
                  )}
                  <Badge tone="danger">{p.daysSinceContact} días</Badge>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Sprints: activo + histórico de velocity ── */}
      {(bySprintActive.length > 0 || sprintHistory.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="nx-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target size={15} className="text-accent" />
              <h2 className="nx-h2">Sprint activo</h2>
              <InfoTip
                text="El hito en curso: avance de tareas (velocity) + su objetivo (activaciones logradas vs la meta del hito). El trabajo y la meta del período, juntos."
                className="ml-auto"
              />
            </div>
            {bySprintActive.length === 0 ? (
              <p className="text-sm text-fg-subtle">Sin sprints activos.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {bySprintActive.map((s) => (
                  <div key={s.sprintId} className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-fg">{s.sprintName}</span>
                      <Badge tone="info">{s.percentComplete}%</Badge>
                      <span className="text-2xs text-fg-subtle ml-auto">
                        {s.completedCount}/{s.taskCount} · faltan {s.remaining}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-bg-subtle overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent"
                        style={{ width: `${s.percentComplete}%` }}
                      />
                    </div>
                    {s.byPerson.filter((p) => p.total > 0).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-0.5">
                        {s.byPerson
                          .filter((p) => p.total > 0)
                          .map((p) => (
                            <span
                              key={String(p.assigneeUserId)}
                              className="text-2xs text-fg-muted inline-flex items-center gap-1"
                            >
                              <Avatar name={p.assignee?.name ?? "Sin asignar"} size={16} />
                              {p.completed}/{p.total}
                            </span>
                          ))}
                      </div>
                    )}

                    {/* Objetivo del hito: activaciones en la ventana vs meta. */}
                    {(s.segmentProgress ?? []).some((g) => g.target > 0 || g.achieved > 0) && (
                      <div className="mt-1.5 pt-1.5 border-t border-border flex flex-col gap-1">
                        <span className="text-2xs text-fg-subtle">Objetivo del hito (activaciones)</span>
                        {(s.segmentProgress ?? [])
                          .filter((g) => g.target > 0 || g.achieved > 0)
                          .map((g) => {
                            const pct =
                              g.target > 0 ? Math.min(100, Math.round((g.achieved / g.target) * 100)) : 0;
                            return (
                              <div key={g.segment} className="flex items-center gap-2">
                                <span className="text-2xs text-fg-muted w-20 shrink-0">{g.label}</span>
                                <div className="h-1.5 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-success"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                                <span className="text-2xs tabular-nums text-fg-muted w-16 text-right shrink-0">
                                  {g.achieved}/{g.target || "—"}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="nx-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={15} className="text-accent" />
              <h2 className="nx-h2">Velocity histórico</h2>
              <InfoTip
                text="Tareas completadas en cada hito ya cerrado. Sirve para ver el ritmo del equipo en el tiempo."
                className="ml-auto"
              />
            </div>
            {sprintHistory.length === 0 ? (
              <p className="text-sm text-fg-subtle">Todavía no cerraste ningún hito.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {sprintHistory.map((h) => {
                  const vel = h.performanceSnapshot?.velocity ?? 0;
                  const pct = h.performanceSnapshot?.percentComplete ?? 0;
                  return (
                    <div key={h.sprintId} className="flex items-center gap-3">
                      <span className="text-sm text-fg w-32 truncate shrink-0">{h.name}</span>
                      <div className="h-2 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                        <div
                          className="h-full rounded-full bg-success"
                          style={{ width: `${(vel / maxVel) * 100}%` }}
                        />
                      </div>
                      <span className="text-2xs tabular-nums text-fg-muted w-24 text-right shrink-0">
                        {vel} hechas · {pct}%
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Performance de tareas del equipo ── */}
      {byAssignee.length > 0 && (
        <div className="nx-card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border bg-bg-surface flex items-center gap-2">
            <Users size={15} className="text-accent" />
            <h2 className="text-sm font-medium text-fg">Performance por persona</h2>
            <span className="text-2xs text-fg-subtle ml-auto">
              Completitud sobre trabajo comprometido (excluye Backlog)
            </span>
            <InfoTip
              text="Detalle de ejecución por persona: tareas activas, hechas, en curso, vencidas y % de cierre. Para ver cómo avanza cada uno (versión detallada de 'Aportes por cada uno')."
              className="ml-1.5"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th style={TH_CENTER}>Activas</th>
                  <th style={TH_CENTER}>Hechas</th>
                  <th style={TH_CENTER}>En curso</th>
                  <th style={TH_CENTER}>Vencidas</th>
                  <th style={{ minWidth: 160 }}>Completitud</th>
                </tr>
              </thead>
              <tbody>
                {byAssignee.map((p) => (
                  <tr key={String(p.assigneeUserId)}>
                    <td>
                      <div className="flex items-center gap-2">
                        <Avatar name={p.assignee?.name ?? "Sin asignar"} size={22} />
                        <span className="text-fg">{p.assignee?.name ?? "Sin asignar"}</span>
                      </div>
                    </td>
                    <td className="text-center tabular-nums">{p.active}</td>
                    <td className="text-center tabular-nums text-success">{p.completed}</td>
                    <td className="text-center tabular-nums">{p.inProgress}</td>
                    <td
                      className={`text-center tabular-nums ${p.overdue > 0 ? "text-danger font-medium" : "text-fg-subtle"}`}
                    >
                      {p.overdue}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                          <div
                            className="h-full rounded-full bg-accent"
                            style={{ width: `${p.completionRate}%` }}
                          />
                        </div>
                        <span className="text-2xs tabular-nums text-fg-muted w-9 text-right">
                          {p.completionRate}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Áreas de negocio que estamos potenciando ── */}
      {byBusinessArea.length > 0 && (
        <div className="nx-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="nx-h2">Áreas que estamos potenciando</h2>
            <InfoTip
              text="Cómo se reparte el trabajo comprometido por área de negocio. Muestra en qué nos estamos enfocando."
              className="ml-auto"
            />
          </div>
          <p className="text-2xs text-fg-subtle mb-3">
            Distribución del trabajo comprometido por área de negocio.
          </p>
          <div className="flex flex-col gap-2.5">
            {byBusinessArea.map((a) => (
              <div key={String(a.businessAreaId)} className="flex items-center gap-3">
                <span className="flex items-center gap-2 w-32 shrink-0">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ background: a.colorHex ?? "var(--nx-border, #ccc)" }}
                  />
                  <span className="text-sm text-fg truncate">{a.areaName}</span>
                </span>
                <div className="h-2 flex-1 rounded-full bg-bg-subtle overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${a.share}%`, background: a.colorHex ?? "var(--nx-accent, #888)" }}
                  />
                </div>
                <span className="text-2xs tabular-nums text-fg-muted w-28 text-right shrink-0">
                  {a.share}% · {a.completedCount}/{a.activeCount} hechas
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Funnel comercial — matriz etapa × tipo */}
      <div className="nx-card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-border bg-bg-surface flex items-center gap-2">
          <h2 className="text-sm font-medium text-fg">Funnel comercial — prospectos por etapa</h2>
          <InfoTip
            text="Prospectos por etapa y por tipo (artista/productor/proveedor). La foto completa del embudo comercial."
            className="ml-auto"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="nx-table">
            <thead>
              <tr>
                <th>Etapa</th>
                <th style={TH_CENTER}>Artistas</th>
                <th style={TH_CENTER}>Productores</th>
                <th style={TH_CENTER}>Proveedores</th>
                <th style={TH_CENTER}>Total</th>
              </tr>
            </thead>
            <tbody>
              {matrixRows.map((r) => (
                <tr key={r.stage}>
                  <td>
                    <Badge tone={STAGE_TONE[r.stage as ProspectStage]}>
                      {STAGE_LABELS[r.stage as ProspectStage]}
                    </Badge>
                  </td>
                  <td className="text-center tabular-nums">{r.artista}</td>
                  <td className="text-center tabular-nums">{r.productor}</td>
                  <td className="text-center tabular-nums">{r.proveedor}</td>
                  <td className="text-center tabular-nums font-medium text-fg">{r.total}</td>
                </tr>
              ))}
              <tr className="font-medium">
                <td className="text-fg">Total</td>
                <td className="text-center tabular-nums">{m.funnel.byType.artista ?? 0}</td>
                <td className="text-center tabular-nums">{m.funnel.byType.productor ?? 0}</td>
                <td className="text-center tabular-nums">{m.funnel.byType.proveedor ?? 0}</td>
                <td className="text-center tabular-nums">{m.funnel.total}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Estado de módulos. (El detalle de aportes por persona vive en
          "Performance por persona" — "Aportes por cada uno" mostraba lo mismo y
          se removió para no duplicar.) */}
      <div className="nx-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="nx-h2">Estado de módulos</h2>
          <InfoTip
            text="Cuántos módulos de la plataforma están OK, en prueba, sin probar o fallando. El estado de calidad del producto."
            className="ml-auto"
          />
        </div>
        <div className="flex flex-col gap-2">
          {MODULE_STATUS_ORDER.map((k) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <Badge tone={RESULT_TONE[k]}>{RESULT_LABELS[k]}</Badge>
              <span className="tabular-nums text-fg">{m.modules.byStatus[k] ?? 0}</span>
            </div>
          ))}
          <div className="flex items-center justify-between text-xs text-fg-subtle pt-1 border-t border-border mt-1">
            <span>Total módulos</span>
            <span className="tabular-nums">{m.modules.total}</span>
          </div>
        </div>
      </div>

      {/* Pendientes de seguimiento por responsable (motor unificado: prospectos + invitaciones) */}
      {(m.pending?.byResponsable ?? []).length > 0 && (
        <div className="nx-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="nx-h2">Pendientes de seguimiento por responsable</h2>
            <InfoTip
              text="Prospectos sin contacto + invitaciones sin avanzar, agrupados por responsable. Para repartir y ordenar el seguimiento."
              className="ml-auto"
            />
          </div>
          <p className="text-2xs text-fg-subtle mb-3">
            Prospectos sin contacto + invitaciones sin avanzar. Total: {m.pending?.total ?? 0}.
          </p>
          <div className="flex flex-col gap-2">
            {(m.pending?.byResponsable ?? []).map((r) => (
              <div key={String(r.responsableUserId)} className="flex items-center gap-2 text-sm">
                <Avatar name={r.name} />
                <span className="text-fg">{r.name}</span>
                <span className="ml-auto">
                  <Badge tone={r.count > 0 ? "danger" : "neutral"}>{r.count}</Badge>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
